import React, { useEffect, useState } from "react";
import { Zap, WifiOff, Bell } from "lucide-react";
import logo from "../assets/canluxa-logo.png";

function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();

    // FIX 1: Proper iOS detection (was hardcoded `true`)
    const ios = /iphone|ipad|ipod/.test(userAgent) && !window.MSStream;
    // const ios = true

    // FIX 2: Also check navigator.standalone for iOS Safari
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;

    setIsIOS(ios);
    setIsStandalone(standalone);

    if (ios && !standalone) {
      // FIX 3: Don't show again if user already dismissed (localStorage persist)
      const alreadyDismissed = localStorage.getItem("pwa-ios-dismissed");
      if (!alreadyDismissed) {
        setTimeout(() => setShowInstall(true), 1500);
      }
    }

    // FIX 4: Store handler ref so we can removeEventListener on cleanup
    const handler = (e) => {
      e.preventDefault();
      // FIX 5: Don't show if already dismissed in this session
      const alreadyDismissed = localStorage.getItem("pwa-android-dismissed");
      if (!alreadyDismissed) {
        setDeferredPrompt(e);
        setTimeout(() => setShowInstall(true), 1500);
      }
    };

    window.addEventListener("beforeinstallprompt", handler);

    // FIX 6: Cleanup event listener on unmount (was missing — memory leak)
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);

    try {
      deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;

      if (choiceResult.outcome === "accepted") {
        console.log("User accepted install");
        // FIX 7: Mark as installed so prompt never shows again
        localStorage.setItem("pwa-android-dismissed", "installed");
      } else {
        // User declined — remember for this session only
        sessionStorage.setItem("pwa-android-declined", "true");
      }
    } catch (err) {
      // FIX 8: Handle prompt() errors gracefully (e.g. called twice)
      console.warn("Install prompt error:", err);
    }

    setDeferredPrompt(null);
    setInstalling(false);
    setShowInstall(false);
  };

  const handleDismiss = () => {
    setDismissed(true);

    // FIX 9: Persist dismissal so prompt doesn't re-appear on page refresh
    if (isIOS) {
      localStorage.setItem("pwa-ios-dismissed", "true");
    }

    setTimeout(() => {
      setShowInstall(false);
      setDismissed(false);
    }, 300);
  };

  if (isStandalone || !showInstall) return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600&family=DM+Sans:wght@300;400;500&display=swap');

        @keyframes slideUp {
          from { transform: translateY(120%) scale(0.97); opacity: 0; }
          to   { transform: translateY(0%)  scale(1);    opacity: 1; }
        }
        @keyframes slideDown {
          from { transform: translateY(0%)   scale(1);    opacity: 1; }
          to   { transform: translateY(120%) scale(0.97); opacity: 0; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes pulse-ring {
          0%   { box-shadow: 0 0 0 0 rgba(74,163,93,0.35); }
          70%  { box-shadow: 0 0 0 10px rgba(74,163,93,0); }
          100% { box-shadow: 0 0 0 0  rgba(74,163,93,0); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
        @keyframes stepIn {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        /* FIX 10: Loading spinner for installing state */
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .pwa-backdrop {
          position: fixed; inset: 0;
          background: rgba(10,28,12,0.45);
          backdrop-filter: blur(3px);
          z-index: 9998;
          animation: fadeIn 0.3s ease forwards;
        }
        .pwa-backdrop.leaving { animation: fadeIn 0.25s ease reverse forwards; }

        .pwa-card {
          position: fixed;
          bottom: 0; left: 0; right: 0;
          z-index: 9999;
          font-family: 'DM Sans', sans-serif;
          animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) forwards;
        }
        .pwa-card.leaving { animation: slideDown 0.3s cubic-bezier(0.4,0,1,1) forwards; }

        .pwa-inner {
          background: #fafaf8;
          border-radius: 28px 28px 0 0;
          /* FIX 11: Correct safe-area padding for notch/home-bar devices */
          padding-bottom: env(safe-area-inset-bottom, 16px);
          overflow: hidden;
          box-shadow: 0 -8px 48px rgba(10,40,14,0.18), 0 -2px 12px rgba(10,40,14,0.08);
        }

        .pwa-accent-bar {
          height: 4px;
          background: linear-gradient(90deg, #1a5c2a 0%, #4aa35d 50%, #1a5c2a 100%);
          background-size: 200% auto;
          animation: shimmer 2.5s linear infinite;
        }

        .pwa-handle {
          width: 40px; height: 4px;
          background: #d1d5c9;
          border-radius: 2px;
          margin: 14px auto 0;
        }

        .pwa-header {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 20px 24px 4px;
        }

        .pwa-logo-wrap {
          width: 52px; height: 52px;
          background: linear-gradient(135deg, #1a5c2a 0%, #2d8a3e 100%);
          border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          animation: pulse-ring 2.5s ease-out infinite;
        }
        .pwa-logo-wrap svg { width: 28px; height: 28px; }

        .pwa-title-group { flex: 1; }
        .pwa-brand {
          font-family: 'Playfair Display', serif;
          font-size: 18px; font-weight: 600;
          color: #0e2112; letter-spacing: -0.3px; line-height: 1.2;
        }
        .pwa-tagline {
          font-size: 11px; font-weight: 500;
          letter-spacing: 1.5px; text-transform: uppercase;
          color: #4aa35d; margin-top: 1px;
        }

        .pwa-dismiss-btn {
          width: 30px; height: 30px;
          background: #ebebe6; border: none; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; flex-shrink: 0;
          transition: background 0.2s;
        }
        .pwa-dismiss-btn:hover { background: #dcdcd6; }

        .pwa-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, #dde8dd, transparent);
          margin: 16px 24px;
        }

        /* ── Android ── */
        .pwa-android-body { padding: 0 24px 24px; }

        .pwa-headline {
          font-family: 'Playfair Display', serif;
          font-size: 22px; font-weight: 500;
          color: #0e2112; line-height: 1.3; margin-bottom: 6px;
        }
        .pwa-sub {
          font-size: 13px; color: #5a6b5e;
          font-weight: 400; line-height: 1.5; margin-bottom: 20px;
        }

        .pwa-features { display: flex; gap: 10px; margin-bottom: 22px; }
        .pwa-feature-pill {
          flex: 1;
          background: #f0f5f0; border: 1px solid #d4e8d4;
          border-radius: 10px; padding: 10px 8px; text-align: center;
        }
        .pwa-feature-icon { margin-bottom: 5px; display: flex; justify-content: center; }
        .pwa-feature-label {
          font-size: 10px; font-weight: 600;
          color: #2d6b3a; letter-spacing: 0.2px;
          margin-bottom: 2px;
        }
        /* FIX 12: Added feature description text (was missing in original) */
        .pwa-feature-desc {
          font-size: 9.5px; color: #7a9a7e; line-height: 1.3;
        }

        .pwa-install-btn {
          width: 100%;
          background: linear-gradient(135deg, #1a5c2a 0%, #2d8a3e 100%);
          color: #fff; border: none; border-radius: 14px;
          padding: 15px;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px; font-weight: 500; letter-spacing: 0.3px;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: transform 0.15s, box-shadow 0.15s;
          box-shadow: 0 4px 20px rgba(26,92,42,0.35);
          margin-bottom: 10px;
        }
        .pwa-install-btn:hover  { transform: translateY(-1px); box-shadow: 0 6px 24px rgba(26,92,42,0.4); }
        .pwa-install-btn:active { transform: translateY(1px);  box-shadow: 0 2px 12px rgba(26,92,42,0.3); }
        .pwa-install-btn:disabled { opacity: 0.7; cursor: not-allowed; transform: none; }

        /* FIX 10: Real spinner instead of static icon */
        .pwa-spinner {
          width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.35);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }

        .pwa-later-btn {
          width: 100%; background: transparent; border: none;
          color: #8a9e8d; font-size: 13px; font-weight: 400;
          cursor: pointer; padding: 6px;
          font-family: 'DM Sans', sans-serif;
          transition: color 0.2s;
        }
        .pwa-later-btn:hover { color: #5a6b5e; }

        .pwa-security {
          display: flex; align-items: center; justify-content: center;
          gap: 5px; margin-top: 14px;
          font-size: 11px; color: #a0b0a3; font-weight: 400;
        }
        .pwa-security svg { width: 11px; height: 11px; }

        /* ── iOS ── */
        .pwa-ios-body { padding: 0 24px 24px; }
        .pwa-steps { display: flex; flex-direction: column; gap: 0; margin-bottom: 22px; }

        .pwa-step {
          display: flex; align-items: flex-start; gap: 12px;
          opacity: 0;
          animation: stepIn 0.4s ease forwards;
          position: relative;
        }
        .pwa-step:nth-child(1) { animation-delay: 0.1s; }
        .pwa-step:nth-child(2) { animation-delay: 0.22s; }
        .pwa-step:nth-child(3) { animation-delay: 0.34s; }

        /* FIX 13: Connector line between iOS steps (visual clarity) */
        .pwa-step:not(:last-child)::before {
          content: '';
          position: absolute;
          left: 13px; top: 30px;
          width: 2px; height: calc(100% + 4px);
          background: linear-gradient(to bottom, #c2dbc5, transparent);
          z-index: 0;
        }

        .pwa-step-num {
          width: 28px; height: 28px; flex-shrink: 0;
          background: linear-gradient(135deg, #1a5c2a, #2d8a3e);
          color: #fff; border-radius: 8px;
          font-size: 13px; font-weight: 600;
          display: flex; align-items: center; justify-content: center;
          font-family: 'DM Sans', sans-serif;
          margin-top: 2px;
          position: relative; z-index: 1;
        }

        .pwa-step-content { padding-bottom: 16px; }
        .pwa-step-label { font-size: 13px; font-weight: 500; color: #0e2112; margin-bottom: 2px; }
        .pwa-step-desc { font-size: 12px; color: #7a8e7d; line-height: 1.4; }
        .pwa-step-highlight {
          display: inline-flex; align-items: center; gap: 3px;
          background: #eaf3eb; color: #2d6b3a;
          font-size: 11px; font-weight: 600;
          padding: 2px 7px; border-radius: 5px;
          border: 1px solid #c2dbc5;
        }

        /* FIX 14: iOS close button styled like Android install btn for consistency */
        .pwa-ios-close {
          width: 100%;
          background: linear-gradient(135deg, #1a5c2a 0%, #2d8a3e 100%);
          border: none; border-radius: 14px;
          color: #fff;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px; font-weight: 500;
          padding: 15px; cursor: pointer;
          box-shadow: 0 4px 20px rgba(26,92,42,0.35);
          transition: transform 0.15s, box-shadow 0.15s;
        }
        .pwa-ios-close:hover { transform: translateY(-1px); box-shadow: 0 6px 24px rgba(26,92,42,0.4); }
      `}</style>

      {/* Backdrop */}
      <div
        className={`pwa-backdrop${dismissed ? " leaving" : ""}`}
        onClick={handleDismiss}
        role="presentation"
      />

      {/* Card */}
      <div
        className={`pwa-card${dismissed ? " leaving" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={isIOS ? "Add Canluxa to Home Screen" : "Install Canluxa App"}
      >
        <div className="pwa-inner">
          <div className="pwa-accent-bar" />
          <div className="pwa-handle" />

          {/* Header — shared */}
          <div className="pwa-header">
            <div className="pwa-logo-wrap" aria-hidden="true">
              {/* <svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="28" height="28" rx="6" fill="white" fillOpacity="0.15"/>
                <path d="M14 5L22 22H6L14 5Z" stroke="white" strokeWidth="2" strokeLinejoin="round" fill="none"/>
                <path d="M10 16H18" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
              </svg> */}
                      <img src={logo} alt="Canluxa" style={{ height: "40px" }} />
            </div>
            <div className="pwa-title-group">
              <div className="pwa-brand">Canluxa</div>
              <div className="pwa-tagline">Valet Trash</div>
            </div>
            <button
              className="pwa-dismiss-btn"
              onClick={handleDismiss}
              aria-label="Close install prompt"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1 1l10 10M11 1L1 11" stroke="#888" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          <div className="pwa-divider" />

          {/* ── iOS Flow ── */}
          {isIOS ? (
            <div className="pwa-ios-body">
              <div className="pwa-headline">Add to Home Screen</div>
              <div className="pwa-sub">
                3 quick steps to install Canluxa on your iPhone.
              </div>

              <div className="pwa-steps">
                <div className="pwa-step">
                  <div className="pwa-step-num">1</div>
                  <div className="pwa-step-content">
                    <div className="pwa-step-label">Open the Share Menu</div>
                    <div className="pwa-step-desc">
                      Tap the{" "}
                      <span className="pwa-step-highlight">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/>
                        </svg>
                        Share
                      </span>{" "}
                      icon at the bottom of Safari
                    </div>
                  </div>
                </div>

                <div className="pwa-step">
                  <div className="pwa-step-num">2</div>
                  <div className="pwa-step-content">
                    <div className="pwa-step-label">Select Add to Home Screen</div>
                    <div className="pwa-step-desc">
                      Scroll down and tap{" "}
                      <span className="pwa-step-highlight">＋ Add to Home Screen</span>
                    </div>
                  </div>
                </div>

                <div className="pwa-step">
                  <div className="pwa-step-num">3</div>
                  <div className="pwa-step-content">
                    <div className="pwa-step-label">Confirm & Launch</div>
                    <div className="pwa-step-desc">
                      Tap <span className="pwa-step-highlight">Add</span> — Canluxa will appear on your home screen instantly.
                    </div>
                  </div>
                </div>
              </div>

              <button className="pwa-ios-close" onClick={handleDismiss}>
                Got it ✓
              </button>
            </div>

          ) : (
            /* ── Android Flow ── */
            <div className="pwa-android-body">
              <div className="pwa-headline">Install the App</div>
              <div className="pwa-sub">
                Get the full Canluxa experience — faster, offline-ready, and always one tap away.
              </div>

              {/* Feature pills — Lucide React icons */}
              <div className="pwa-features">
                {/* Instant Load */}
                <div className="pwa-feature-pill">
                  <div className="pwa-feature-icon">
                    <div className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center mx-auto">
                      <Zap size={18} className="text-amber-500" strokeWidth={2.5} fill="currentColor" />
                    </div>
                  </div>
                  <div className="pwa-feature-label">Instant Load</div>
                  <div className="pwa-feature-desc">Opens in under 1s</div>
                </div>

                {/* Works Offline */}
                <div className="pwa-feature-pill">
                  <div className="pwa-feature-icon">
                    <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center mx-auto">
                      <WifiOff size={18} className="text-blue-500" strokeWidth={2.5} />
                    </div>
                  </div>
                  <div className="pwa-feature-label">Works Offline</div>
                  <div className="pwa-feature-desc">No internet needed</div>
                </div>

                {/* Notifications */}
                <div className="pwa-feature-pill">
                  <div className="pwa-feature-icon">
                    <div className="w-9 h-9 rounded-xl bg-green-50 border border-green-100 flex items-center justify-center mx-auto">
                      <Bell size={18} className="text-green-600" strokeWidth={2.5} fill="currentColor" />
                    </div>
                  </div>
                  <div className="pwa-feature-label">Notifications</div>
                  <div className="pwa-feature-desc">Never miss a pickup</div>
                </div>
              </div>

              <button
                className="pwa-install-btn"
                onClick={handleInstallClick}
                disabled={installing}
              >
                {installing ? (
                  /* FIX 10: Real spinner animation */
                  <>
                    <div className="pwa-spinner" />
                    Installing…
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                    </svg>
                    Install Canluxa
                  </>
                )}
              </button>

              <button className="pwa-later-btn" onClick={handleDismiss}>
                Maybe later
              </button>

              <div className="pwa-security">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <rect x="3" y="11" width="18" height="11" rx="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
                No app store required · Secure installation
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default InstallPWA;